import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  Alert
} from '@mui/material';
import {
  People,
  BeachAccess,
  AccessTime,
  Approval,
  Login,
  Work,
  ArrowForward,
  Event,
  CalendarMonth
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useHRStore from '@/store/DashboardStore';
import FeedWidget from '@/components/OrganizationFeed/FeedWidget';
import {
  DashboardHeader,
  StatTile,
  SmartAlertsWidget,
  QuickActionsBar,
  WallOfFameWidget,
  WorkforceAnalyticsWidget,
  MiniCalendar,
  getHRQuickActions
} from '@/components/Dashboard';
import {
  getWorkforceAnalytics,
  getHRKPIs,
  getRecentRecognitions,
  getMyAlerts,
  dismissAlert,
  getCompanySettings,
  getJobStats,
  getUpcomingHolidays,
  getUpcomingBirthdays,
  WorkforceAnalytics,
  HRKPIs,
  Recognition,
  Alert as AlertType,
  CompanySettings,
  JobStats,
  Holiday,
  Birthday
} from '@/services/DashboardAnalyticsService';

// Type definitions
interface AttendanceRecord {
  attendance_id: number;
  employee_name: string;
  punch_in: string;
  punch_out: string | null;
  daily_status: string;
}

const HRDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Get data from HRStore
  const {
    todayAttendance,
    upcomingLeaves,
    dashboardStats,
    trackerCounts,
    loading: storeLoading,
    error: storeError,
    fetchTodayAttendance,
    fetchUpcomingLeaves,
    fetchDashboardStats,
    fetchTrackerCounts
  } = useHRStore();

  // Local state for new features
  const [workforceData, setWorkforceData] = useState<WorkforceAnalytics | null>(null);
  const [hrKPIs, setHRKPIs] = useState<HRKPIs | null>(null);
  const [recognitions, setRecognitions] = useState<Recognition[]>([]);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [jobStats, setJobStats] = useState<JobStats | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const typedAttendance: AttendanceRecord[] = todayAttendance || [];
  const typedUpcomingLeaves = upcomingLeaves || [];

  // Fetch existing data
  useEffect(() => {
    fetchTodayAttendance();
    fetchUpcomingLeaves();
    fetchDashboardStats();
    fetchTrackerCounts();
  }, [fetchTodayAttendance, fetchUpcomingLeaves, fetchDashboardStats, fetchTrackerCounts]);

  // Fetch new analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError(null);

      try {
        const [
          workforceRes,
          kpisRes,
          recognitionsRes,
          alertsRes,
          settingsRes,
          jobsRes,
          holidaysRes,
          birthdaysRes
        ] = await Promise.all([
          getWorkforceAnalytics(),
          getHRKPIs(),
          getRecentRecognitions(4),
          getMyAlerts(1, 5),
          getCompanySettings(),
          getJobStats(),
          getUpcomingHolidays(5),
          getUpcomingBirthdays()
        ]);

        if (workforceRes.success && workforceRes.data) {
          setWorkforceData(workforceRes.data);
        }
        if (kpisRes.success && kpisRes.data) {
          setHRKPIs(kpisRes.data);
        }
        if (recognitionsRes.success && recognitionsRes.data) {
          setRecognitions(recognitionsRes.data);
        }
        if (alertsRes.success && alertsRes.data) {
          setAlerts(alertsRes.data);
        }
        if (settingsRes.success && settingsRes.data) {
          setCompanySettings(settingsRes.data);
        }
        if (jobsRes.success && jobsRes.data) {
          setJobStats(jobsRes.data);
        }
        if (holidaysRes.success && holidaysRes.data) {
          setHolidays(holidaysRes.data);
        }
        if (birthdaysRes.success && birthdaysRes.data) {
          setBirthdays(birthdaysRes.data);
        }
      } catch (error: any) {
        setAnalyticsError(error.message || 'Failed to load analytics');
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const handleRefresh = () => {
    fetchTodayAttendance();
    fetchUpcomingLeaves();
    fetchDashboardStats();
    fetchTrackerCounts();
  };

  const handleDismissAlert = async (id: number) => {
    const result = await dismissAlert(id);
    if (result.success) {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }
  };

  // Quick actions handlers
  const quickActionsHandlers = {
    onAddEmployee: () => navigate('/employees'),
    onPostJob: () => navigate('/hr/jobs'),
    onApproveLeaves: () => navigate('/leaves?tab=pending'),
    onPostAnnouncement: () => navigate('/organization-feed'),
    onGiveRecognition: () => navigate('/hr/recognitions'),
    onViewAttendance: () => navigate('/daily-tracker')
  };

  const quickActions = getHRQuickActions(quickActionsHandlers);

  // Statistics from existing store
  const totalEmployees = dashboardStats?.employee_stats?.total_employees || hrKPIs?.totalHeadcount || 0;
  const punchedInToday = dashboardStats?.attendance_stats?.present_today || 0;
  const pendingLeaves = dashboardStats?.leave_stats?.pending_leaves || hrKPIs?.pendingLeaveRequests || 0;
  const openPositions = jobStats?.open_positions || hrKPIs?.openJobPositions || 0;

  // Calculate attendance rate
  const attendanceRate = totalEmployees > 0
    ? Math.round((punchedInToday / totalEmployees) * 100)
    : 0;

  // Helper to format date string
  const formatHolidayDate = (dateStr: string): string => {
    const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = cleanDate.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return `${weekdays[date.getDay()]}, ${months[month - 1]} ${day}`;
  };

  // Calendar events from holidays and birthdays
  const calendarEvents = [
    ...holidays.map(h => ({
      date: h.date.includes('T') ? h.date.split('T')[0] : h.date,
      type: 'holiday' as const,
      title: h.name
    })),
    ...birthdays.map(b => ({
      date: b.birthday_this_year,
      type: 'birthday' as const,
      title: `${b.name}'s Birthday`
    }))
  ];

  // Loading state
  if (storeLoading && analyticsLoading) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          {/* Dashboard Header */}
          <DashboardHeader
            companyName={companySettings?.company_name || 'Nucleus'}
            companyLogo={companySettings?.company_logo_url}
            showRefresh
            onRefresh={handleRefresh}
            loading={analyticsLoading}
          />

          {/* Error Alert */}
          {(storeError || analyticsError) && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setAnalyticsError(null)}>
              {storeError || analyticsError}
            </Alert>
          )}

          {/* Stats Tiles Row */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 2,
            mb: 4
          }}>
            <StatTile
              title="Total Headcount"
              value={totalEmployees}
              icon={<People />}
              variant="primary"
              onClick={() => navigate('/employees')}
              tooltip="View all employees"
            />
            <StatTile
              title="Open Positions"
              value={openPositions}
              icon={<Work />}
              variant="success"
              onClick={() => navigate('/hr/jobs')}
              subtitle={jobStats?.open_jobs ? `${jobStats.open_jobs} jobs` : undefined}
            />
            <StatTile
              title="Attendance Today"
              value={`${attendanceRate}%`}
              icon={<Login />}
              variant="info"
              onClick={() => navigate('/daily-tracker')}
              subtitle={`${punchedInToday} present`}
            />
            <StatTile
              title="Pending Approvals"
              value={pendingLeaves}
              icon={<Approval />}
              variant="warning"
              onClick={() => navigate('/leaves?tab=pending')}
              tooltip="Leave requests awaiting approval"
            />
            <StatTile
              title="On Leave Today"
              value={dashboardStats?.leave_stats?.on_leave_today || 0}
              icon={<BeachAccess />}
              variant="error"
              onClick={() => navigate('/leaves')}
            />
          </Box>

          {/* Main Content Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            gap: 3,
            mb: 4
          }}>
            {/* Workforce Analytics */}
            <WorkforceAnalyticsWidget
              departmentData={workforceData?.departmentDistribution || []}
              genderData={workforceData?.genderDistribution || []}
              experienceData={workforceData?.experienceDistribution || []}
              ageData={workforceData?.ageDistribution || []}
              loading={analyticsLoading}
            />

            {/* Smart Alerts */}
            <SmartAlertsWidget
              alerts={alerts}
              loading={analyticsLoading}
              unreadCount={alerts.filter(a => !a.is_read).length}
              onDismiss={handleDismissAlert}
              onViewAll={() => navigate('/hr/alerts')}
              onAlertClick={(alert) => alert.action_url && navigate(alert.action_url)}
            />
          </Box>

          {/* Second Row - Attendance, Leaves, Wall of Fame */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 4
          }}>
            {/* Today's Attendance Summary */}
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Today's Attendance
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/daily-tracker')}
                    sx={{ textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Box>

                {/* Punch Stats */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 1
                }}>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Punched In</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {trackerCounts?.punched_in_count || 0}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Punched Out</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {trackerCounts?.punched_out_count || 0}
                    </Typography>
                  </Box>
                </Box>

                <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {typedAttendance.length > 0 ? typedAttendance.slice(0, 4).map((attendance, index) => (
                    <React.Fragment key={attendance.attendance_id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 36, height: 36, fontSize: '0.875rem' }}>
                            {attendance.employee_name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={attendance.employee_name}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondary={`In: ${attendance.punch_in ? new Date(attendance.punch_in).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-'}`}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip
                          label={attendance.punch_out ? 'Done' : 'Active'}
                          size="small"
                          color={attendance.punch_out ? 'success' : 'warning'}
                          sx={{ height: 22 }}
                        />
                      </ListItem>
                      {index < typedAttendance.slice(0, 4).length - 1 && <Divider />}
                    </React.Fragment>
                  )) : (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <AccessTime sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                      <Typography variant="body2">No attendance data today</Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Upcoming Leaves */}
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Upcoming Leaves
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/leaves?tab=upcoming')}
                    sx={{ textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Box>

                <List sx={{ maxHeight: 280, overflow: 'auto' }}>
                  {typedUpcomingLeaves.length > 0 ? typedUpcomingLeaves.slice(0, 4).map((leave: any, index: number) => (
                    <React.Fragment key={leave.id || index}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 36, height: 36, fontSize: '0.875rem', bgcolor: 'warning.main' }}>
                            {leave.name ? leave.name.split(' ').map((n: string) => n[0]).join('') : 'U'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={leave.name || 'Unknown'}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondary={
                            leave.startDate && leave.endDate
                              ? `${new Date(leave.startDate).toLocaleDateString()} - ${new Date(leave.endDate).toLocaleDateString()}`
                              : 'Date not available'
                          }
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        {leave.type && (
                          <Chip label={leave.type} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                        )}
                      </ListItem>
                      {index < typedUpcomingLeaves.slice(0, 4).length - 1 && <Divider />}
                    </React.Fragment>
                  )) : (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <BeachAccess sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                      <Typography variant="body2">No upcoming leaves</Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Wall of Fame */}
            <WallOfFameWidget
              recognitions={recognitions}
              loading={analyticsLoading}
              canAddRecognition
              onAddRecognition={() => navigate('/hr/recognitions?create=true')}
              onViewAll={() => navigate('/hr/recognitions')}
              maxItems={4}
            />
          </Box>

          {/* Calendar & Holidays Row */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 4
          }}>
            {/* Mini Calendar */}
            <MiniCalendar
              events={calendarEvents}
              onDateClick={(date) => console.log('Clicked date:', date)}
            />

            {/* Upcoming Holidays */}
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Upcoming Holidays
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/holidays')}
                    sx={{ textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Box>

                <List sx={{ p: 0 }}>
                  {holidays.length > 0 ? holidays.slice(0, 4).map((holiday, index) => (
                    <React.Fragment key={holiday.id}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CalendarMonth sx={{ color: theme.palette.error.main }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={holiday.name}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondary={formatHolidayDate(holiday.date)}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip
                          label={holiday.type}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            backgroundColor: holiday.type === 'public' ? '#F4433620' : '#FF980020',
                            color: holiday.type === 'public' ? '#F44336' : '#FF9800'
                          }}
                        />
                      </ListItem>
                      {index < Math.min(holidays.length, 4) - 1 && <Divider />}
                    </React.Fragment>
                  )) : (
                    <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">No upcoming holidays</Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* Organization Feed Widget */}
          <Box sx={{ mb: 4 }}>
            <FeedWidget limit={3} />
          </Box>

          {/* Quick Actions Bar */}
          <QuickActionsBar actions={quickActions} variant="horizontal" />
        </Box>
      </Container>
    </Layout>
  );
};

export default HRDashboard;
